import type { TradeAccountApiRow } from "../services/brokerIntegrationApi";

/**
 * cTrader account naming: platform login (user-facing) vs ctid (sync/API).
 * @see broker Open API account_metadata and GET /broker/trade-accounts ctrader fields.
 */

export type CtraderAccountDisplay = {
  /** ctid — used for sync, active account, trades filter */
  ctid: string;
  /** Platform login the user recognizes (5826993) */
  platformLogin: string | null;
  /** Executing broker brand (FxPro, IC Markets, …) — not the Tessera slug `ctrader` */
  executingBrokerName: string | null;
  depositAssetName?: string | null;
  isLive?: boolean | null;
  environment?: "demo" | "live" | null;
};

function trimOrNull(v: unknown): string | null {
  if (v == null) return null;
  const t = String(v).trim();
  return t || null;
}

function pickFirstTrimmed(...values: unknown[]): string | null {
  for (const v of values) {
    const t = trimOrNull(v);
    if (t) return t;
  }
  return null;
}

function parseLiveFlag(raw: Record<string, unknown>): boolean | null {
  const v = raw.isLive ?? raw.is_live;
  if (v === true || v === false) return v;
  return null;
}

function parseEnvironment(raw: Record<string, unknown>): "demo" | "live" | null {
  const e = String(raw.environment ?? "").trim().toLowerCase();
  if (e === "demo" || e === "live") return e;
  const live = parseLiveFlag(raw);
  if (live === true) return "live";
  if (live === false) return "demo";
  return null;
}

/** OAuth callback `accounts[]` item or nested metadata object. */
export function ctraderDisplayFromRecord(
  raw: Record<string, unknown> | null | undefined
): CtraderAccountDisplay | null {
  if (!raw) return null;
  const ctid = pickFirstTrimmed(
    raw.ctidTraderAccountId,
    raw.ctid_trader_account_id,
    raw.ctrader_ctid,
    raw.ctraderCtid,
    raw.accountCode,
    raw.account_code
  );
  if (!ctid) return null;
  const platformLogin = pickFirstTrimmed(
    raw.platform_account_login,
    raw.platformAccountLogin,
    raw.trader_login,
    raw.traderLogin,
    raw.login
  );
  const executingBrokerName = pickFirstTrimmed(
    raw.executing_broker_name,
    raw.executingBrokerName,
    raw.brokerName,
    raw.broker_name,
    raw.brokerTitle,
    raw.broker_title
  );
  return {
    ctid,
    platformLogin,
    executingBrokerName,
    depositAssetName: pickFirstTrimmed(raw.depositAssetName, raw.deposit_asset_name),
    isLive: parseLiveFlag(raw),
    environment: parseEnvironment(raw),
  };
}

/** Credential payload: top-level fields and/or `account_metadata[ctid]`. */
export function ctraderDisplayFromCredentials(
  credentials: Record<string, unknown> | null | undefined,
  ctidHint?: string | null
): CtraderAccountDisplay | null {
  if (!credentials) return null;
  const ctid =
    pickFirstTrimmed(ctidHint, credentials.account_code, credentials.accountCode) ?? "";
  if (!ctid) return null;

  const metaRoot = credentials.account_metadata ?? credentials.accountMetadata;
  let metaEntry: Record<string, unknown> | null = null;
  if (metaRoot && typeof metaRoot === "object") {
    const entry = (metaRoot as Record<string, unknown>)[ctid];
    if (entry && typeof entry === "object") {
      metaEntry = entry as Record<string, unknown>;
    }
  }

  const fromMeta = metaEntry ? ctraderDisplayFromRecord(metaEntry) : null;
  const fromTop = ctraderDisplayFromRecord({
    ...credentials,
    account_code: ctid,
    accountCode: ctid,
  });

  if (!fromTop && !fromMeta) {
    return { ctid, platformLogin: null, executingBrokerName: null };
  }

  return {
    ctid,
    platformLogin: fromMeta?.platformLogin ?? fromTop?.platformLogin ?? null,
    executingBrokerName:
      fromMeta?.executingBrokerName ?? fromTop?.executingBrokerName ?? null,
    depositAssetName: fromMeta?.depositAssetName ?? fromTop?.depositAssetName ?? null,
    isLive: fromMeta?.isLive ?? fromTop?.isLive ?? null,
    environment: fromMeta?.environment ?? fromTop?.environment ?? null,
  };
}

/** GET /broker/trade-accounts row (cTrader). */
export function ctraderDisplayFromTradeAccountRow(
  row: TradeAccountApiRow
): CtraderAccountDisplay | null {
  const ctid = pickFirstTrimmed(row.ctrader_ctid, row.ctraderCtid, row.account_code);
  if (!ctid) return null;
  const platformLogin = pickFirstTrimmed(row.platform_account_login, row.platformAccountLogin);
  const executingBrokerName = pickFirstTrimmed(row.executing_broker_name, row.executingBrokerName);
  const demoRaw = row.is_demo ?? row.isDemo;
  return {
    ctid,
    platformLogin,
    executingBrokerName,
    isLive: demoRaw == null ? null : !Boolean(demoRaw),
    environment: demoRaw == null ? null : demoRaw ? "demo" : "live",
  };
}

function formatEnvSuffix(display: CtraderAccountDisplay): string {
  if (display.environment === "live") return "Live";
  if (display.environment === "demo") return "Demo";
  if (display.isLive === true) return "Live";
  if (display.isLive === false) return "Demo";
  return "";
}

/** OAuth multi-account picker and similar lists. */
export function formatCtraderOAuthPickerLabel(display: CtraderAccountDisplay): string {
  const login = display.platformLogin;
  const broker = display.executingBrokerName;
  const env = formatEnvSuffix(display);
  const currency = display.depositAssetName;
  const accountPart = login ? `Account ${login}` : `ctid ${display.ctid}`;
  const parts = [broker, accountPart, currency, env].filter(Boolean);
  return parts.join(" · ");
}

/** Trade-account list label when API `label` is absent. */
export function formatCtraderTradeAccountLabel(display: CtraderAccountDisplay): string {
  const broker = display.executingBrokerName || "cTrader";
  const login = display.platformLogin;
  if (login) return `${broker} – ${login}`;
  return `${broker} – ${display.ctid}`;
}

/** Connected-credentials summary on Broker Management tab. */
export function formatCtraderConnectedSummary(display: CtraderAccountDisplay): string {
  const broker = display.executingBrokerName;
  const login = display.platformLogin;
  const env = formatEnvSuffix(display);
  if (broker && login) {
    return `${broker} · account ${login}${env ? ` (${env.toLowerCase()})` : ""}`;
  }
  if (login) return `Account ${login}${env ? ` (${env.toLowerCase()})` : ""}`;
  if (broker) return `${broker}${env ? ` (${env.toLowerCase()})` : ""}`;
  return `ctid ${display.ctid}${env ? ` (${env.toLowerCase()})` : ""}`;
}

export type CtraderSyncHint = {
  headline: string;
  detail: string | null;
};

/** Broker sync panel — login for humans, ctid for API. */
/** Mapped OAuth account from `completeCtraderOAuthCallback`. */
export function ctraderDisplayFromOAuthAccount(account: {
  accountCode: string;
  ctidTraderAccountId?: string;
  traderLogin?: number;
  login?: number;
  executingBrokerName?: string;
  brokerName?: string;
  depositAssetName?: string;
  isLive?: boolean;
  environment?: "demo" | "live";
}): CtraderAccountDisplay {
  const loginNum = account.traderLogin ?? account.login;
  return {
    ctid: account.ctidTraderAccountId || account.accountCode,
    platformLogin: loginNum != null && Number.isFinite(loginNum) ? String(loginNum) : null,
    executingBrokerName:
      trimOrNull(account.executingBrokerName) ?? trimOrNull(account.brokerName),
    depositAssetName: account.depositAssetName ?? null,
    isLive: account.isLive ?? null,
    environment: account.environment ?? null,
  };
}

export function formatCtraderSyncHint(display: CtraderAccountDisplay): CtraderSyncHint {
  const login = display.platformLogin;
  const broker = display.executingBrokerName;
  if (login && broker) {
    return {
      headline: `${broker} · account ${login}`,
      detail: `Open API sync uses ctid ${display.ctid}.`,
    };
  }
  if (login) {
    return {
      headline: `Account ${login}`,
      detail: `Open API sync uses ctid ${display.ctid}.`,
    };
  }
  return {
    headline: `ctid ${display.ctid}`,
    detail: null,
  };
}
